var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/actions.ts
import {
  ItemClass,
  ItemUtils,
  selectors as designSelectors
} from "@ballpark/design-creator";
import { action } from "mobx";
var focus = action(
  (textState, designState, id, point) => {
    const item = designSelectors.getItemById(designState, id);
    if (item === null || ItemUtils.isNotOfType(item, ItemClass.Text)) {
      return;
    }
    textState.focusedPoint = point != null ? point : null;
    textState.focusedId = id;
  }
);
var blur = action((textState) => {
  textState.focusedId = null;
});

// src/components/TextEditor/index.tsx
import { useOnClickOutside } from "@ballpark/design-core";
import {
  useDesign as useDesign3,
  useTransactionCallback
} from "@ballpark/design-plugin-react";
import { observer as observer2 } from "mobx-react";
import { useRef as useRef2 } from "react";

// src/react.tsx
import { useDesign } from "@ballpark/design-plugin-react";
import { createContext, useContext } from "react";
import { useMemoOne } from "use-memo-one";

// src/selectors.ts
import {
  ItemClass as ItemClass2,
  ItemUtils as ItemUtils2,
  selectors as designSelectors2
} from "@ballpark/design-creator";
var getFocusedId = (textState) => textState.focusedId;
var getFocusedPoint = (textState) => textState.focusedPoint;
var getFocusedItem = (textState, designState) => {
  const focusedId = getFocusedId(textState);
  if (focusedId === null)
    return null;
  const item = designSelectors2.getItemById(designState, focusedId);
  if (item === null || ItemUtils2.isNotOfType(item, ItemClass2.Text)) {
    return null;
  }
  return item;
};
var isFocused = (textState, id) => {
  const focusedId = getFocusedId(textState);
  if (id !== void 0) {
    return focusedId === id;
  }
  return focusedId !== null;
};

// src/state.ts
import { observable } from "mobx";
function createTextState() {
  return observable({
    focusedId: null,
    focusedPoint: null
  });
}

// src/react.tsx
import { Fragment, jsx } from "react/jsx-runtime";
function createBoundTextData(designState) {
  const textState = createTextState();
  return {
    // state
    text: textState,
    // selectors
    getFocusedId: () => getFocusedId(textState),
    getFocusedPoint: () => getFocusedPoint(textState),
    getFocusedItem: () => getFocusedItem(textState, designState),
    isFocused: (id) => isFocused(textState, id),
    // actions
    blur: () => blur(textState),
    focus: (id, point) => focus(textState, designState, id, point)
  };
}
var TextContext = createContext(null);
function useText() {
  const state = useContext(TextContext);
  if (state === null)
    throw new Error("useText() called outside of TextProvider");
  return state;
}
function useWeakText() {
  const maybeState = useContext(TextContext);
  const { design } = useDesign();
  const state = useMemoOne(
    () => maybeState != null ? maybeState : createBoundTextData(design),
    [maybeState, design]
  );
  return state;
}
function TextProvider({ children }) {
  const { design } = useDesign();
  const textData = useMemoOne(() => createBoundTextData(design), [design]);
  return /* @__PURE__ */ jsx(TextContext.Provider, { value: textData, children });
}
function WeakTextProvider({ children }) {
  const state = useContext(TextContext);
  if (state === null) {
    return /* @__PURE__ */ jsx(TextProvider, { children });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}

// src/components/TextEditor/TextEditorInner.tsx
import {
  FontStyle,
  TextUtils as TextUtils2
} from "@ballpark/design-creator";
import { useFontLoaded, useWeakFonts } from "@ballpark/design-plugin-fonts";
import { useDesign as useDesign2 } from "@ballpark/design-plugin-react";
import { useWeakView } from "@ballpark/design-plugin-view";

// ../../internal/theme/dist/index.js
function hexToRGB(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgb(${r}, ${g}, ${b})`;
}
var baseColors = {
  marvelDark: "#009EEB",
  marvel: "#1FB6FF",
  marvelLight: "#89DCFF",
  marvelExtraLight: "#B7EAFB",
  greenDark: "#44C553",
  green: "#60D956",
  greenLight: "#87DC72",
  greenExtraLight: "#ADEA9E",
  orange: "#FD6F3C",
  orangeLight: "#FF977A",
  orangeExtraLight: "#FFB6A1",
  orangeExtraExtraLight: "#FFCEBD",
  yellowDark: "#A36300",
  yellow: "#FFAE30",
  yellowLight: "#FFC772",
  yellowExtraLight: "#FFD79B",
  yellowExtraExtraLight: "#FFE3B7",
  purple: "#AD6EDD",
  purpleLight: "#C896EA",
  purpleExtraLight: "#D6AFF1",
  purpleExtraExtraLight: "#DFC7F0",
  teal: "#60D2C9",
  tealLight: "#8FDFDA",
  tealExtraLight: "#BBEDED",
  tealExtraExtraLight: "#DDF6F6",
  licorice: "#0C1014",
  black: "#222D39",
  steel: "#2A3440",
  slate: "#3E4854",
  silver: "#8792A1",
  smokeExtraDark: "#AAB5C5",
  smokeDark: "#C3CCD7",
  smoke: "#D5DCE3",
  snowExtraDark: "#E6E9EF",
  snowDark: "#F0F2F6",
  snow: "#F9FAFB",
  white: "#FFFFFF",
  red: "#F95849",
  redDark: "#E65143",
  twitter: "#55acee",
  facebook: "#3b5999",
  dribbble: "#ea4c89",
  dropbox: "#007ee5",
  google: "#dc4e41",
  instagram: "#3f729b",
  linkedin: "#0077b5",
  pocket: "#ef4056",
  github: "#333",
  youtube: "#FF0000"
};
var green005 = hexToRGB(baseColors.green, 0.05);
var green008 = hexToRGB(baseColors.green, 0.08);
var colors = __spreadProps(__spreadValues({}, baseColors), {
  // We now use Radix colors, which are in the HSL format, so using the hexToRGB
  // util outside of the theme to generate opaque colors is no longer an option.
  // Moving these as real colors in the theme meanwhile to make old components work
  green005,
  green008
});
var black0006 = hexToRGB(colors.black, 6e-3);
var black002 = hexToRGB(colors.black, 0.02);
var black003 = hexToRGB(colors.black, 0.03);
var black005 = hexToRGB(colors.black, 0.05);
var black008 = hexToRGB(colors.black, 0.08);
var black01 = hexToRGB(colors.black, 0.1);
var black012 = hexToRGB(colors.black, 0.12);
var black018 = hexToRGB(colors.black, 0.18);
var black03 = hexToRGB(colors.black, 0.3);
var licorice008 = hexToRGB(colors.licorice, 0.08);
var licorice01 = hexToRGB(colors.licorice, 0.1);
var white04 = hexToRGB(colors.white, 0.4);
var white05 = hexToRGB(colors.white, 0.5);
var shadows = {
  borders: {
    top: `0 -1px 0 0 ${black018}`,
    right: `1px 0 0 0 ${black018}`,
    bottom: `0 1px 0 0 ${black018}`,
    left: `-1px 0 0 0 ${black018}`,
    inset: `inset 0 0 0 1px ${colors.smoke}`,
    insetLight: `inset 0 0 0 1px ${colors.snowExtraDark}`
  },
  button: {
    disabled: `inset 1px 0 0 ${colors.smoke}, inset -1px 0 0 ${colors.smoke}, inset 0 1px 0 ${colors.smoke}, inset 0 -1px 0 ${colors.smokeDark}, 0 1px 2px 0 rgba(12,16,20,0.10)`,
    ghost: `inset 1px 0 0 ${colors.smoke}, inset -1px 0 0 ${colors.smoke}, inset 0 1px 0 ${colors.smoke}, inset 0 -1px 0 ${colors.smokeDark}, 0 1px 2px 0 rgba(12,16,20,0.10)`,
    ghostHover: `inset 1px 0 0 ${colors.smoke}, inset -1px 0 0 ${colors.smoke}, inset 0 1px 0 ${colors.smoke}, inset 0 -1px 0 ${colors.smokeDark}, inset 0 0 0 1px ${colors.smoke}, 0 2px 4px 0 rgba(12,16,20,0.10)`,
    ghostReversed: `inset 0 0 0 1px ${white04}`,
    ghostReversedHover: `inset 0 0 0 1px rgba(255,255,255,0)`,
    withShadow: `0 0 0 1px ${licorice008}, 0 1px 3px 0 ${licorice008}`,
    toolbar: `0 0 0 1px ${black008}`,
    green: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 1px 2px 0 rgba(25,64,22,0.10)`,
    greenHover: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 2px 4px 0 rgba(25,64,22,0.10)`,
    marvel: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 1px 2px 0 rgba(2,47,69,0.10)`,
    marvelHover: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 2px 4px 0 rgba(2,47,69,0.10)`,
    danger: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 1px 2px 0 rgba(53,17,13,0.10)`,
    dangerHover: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 2px 4px 0 rgba(81,25,19,0.10)`,
    text: "0 1px 0 rgba(12,16,20,0.05)",
    // A separate loading state shadow is needed for askhub
    loading: `inset 1px 0 0 ${colors.smoke}, inset -1px 0 0 ${colors.smoke}, inset 0 1px 0 ${colors.smoke}, inset 0 -1px 0 ${colors.smokeDark}, 0 1px 2px 0 rgba(12,16,20,0.10)`
  },
  select: {
    disabled: `inset 0 0 0 1px ${colors.smoke}`,
    ghost: `inset 0 0 0 1px ${colors.smoke}`,
    ghostFocus: `inset 0 0 0 1px ${colors.marvel}`,
    ghostHover: `inset 0 0 0 1px ${colors.smokeExtraDark}`,
    error: `inset 0 0 0 1px ${colors.red}`
  },
  input: {
    default: `inset 0 0 0 1px ${colors.smoke}`,
    defaultTransparent: `0 0 0 1px ${black018}`,
    defaultHover: `inset 0 0 0 1px ${colors.smokeExtraDark}`,
    defaultFocus: `inset 0 0 0 1px ${colors.marvel}`,
    whiteTransparentHover: `inset 0 0 0 1px ${white05}`,
    whiteTransparentFocus: `inset 0 0 0 1px ${colors.white}`,
    error: `inset 0 0 0 1px ${colors.red}`,
    disabled: `inset 0 0 0 1px ${colors.smoke}`,
    underline: `inset 0 -1px 0 ${colors.smoke}`,
    underlineHover: `inset 0 -1px 0 ${colors.smokeExtraDark}`,
    underlineFocus: `inset 0 -1px 0 ${colors.marvel}`
  },
  popover: {
    default: `0 0 1px 0 ${black03}, 0 2px 6px 0 ${black01}`,
    raised: `0 0 0 1px ${black008}, 0 8px 10px 0 ${black008}, 0 8px 24px 0 ${black005}`
  },
  checkbox: {
    default: `inset 0 0 0 1px ${colors.smoke}`,
    defaultChecked: `inset 0 0 0 50% ${colors.green}`
  },
  image: {
    default: `0 0 0 1px ${black012}`,
    selected: `0 0 0 2px ${colors.white}, 0 0 0 4px ${colors.green}`
  },
  modal: `0 6px 12px 0 ${licorice008}`,
  raiseOnHover: `0 0 0 1px ${black0006}, 0 2px 4px 0 ${black003}, 0 8px 16px 0 ${black01}`,
  toast: `0 2px 6px 0 ${licorice01}, 0 3px 15px 0 ${licorice008}`,
  toggleButton: {
    default: `inset 0 0 0 1px ${colors.smoke}`,
    selected: `inset 0 0 0 2px ${colors.marvel}`
  }
};
var easings = {
  bouncy: {
    string: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
    array: [0.68, -0.55, 0.265, 1.55]
  },
  smooth: {
    string: "cubic-bezier(0.23, 1, 0.32, 1)",
    array: [0.23, 1, 0.32, 1]
  }
};
var transitions = {
  smooth: {
    fast: `all 300ms ${easings.smooth.string}`,
    medium: `all 600ms ${easings.smooth.string}`,
    slow: `all 900ms ${easings.smooth.string}`
  }
};

// src/components/TextEditor/TextEditorInner.tsx
import { isHotkey } from "is-hotkey";
import { observer } from "mobx-react";
import { useCallback as useCallback2, useLayoutEffect, useMemo, useState as useState2 } from "react";
import {
  Node,
  Transforms,
  createEditor
} from "slate";
import { HistoryEditor, withHistory } from "slate-history";
import {
  Editable,
  ReactEditor,
  Slate,
  withReact
} from "slate-react";

// src/components/TextEditor/useStylePreview.ts
import { TextUtils } from "@ballpark/design-creator";
import { debounce } from "lodash-es";
import { reaction, toJS } from "mobx";
import { useCallback, useEffect, useRef, useState } from "react";
var STYLE_PREVIEW_TIME_MS = 2e3;
function useStylePreview(textItem) {
  const textStyle = textItem ? TextUtils.getStyle(textItem) : null;
  const isDirty = useRef(false);
  const [previewActive, setPreviewActive] = useState(false);
  const exitPreview = useCallback(() => setPreviewActive(false), []);
  const debounceExitPreview = useCallback(
    debounce(exitPreview, STYLE_PREVIEW_TIME_MS),
    []
  );
  useEffect(
    reaction(
      () => toJS(textStyle),
      () => {
        if (textStyle && !isDirty.current) {
          isDirty.current = true;
          return;
        }
        setPreviewActive(true);
        return () => {
          debounceExitPreview.cancel();
          if (!textStyle) {
            isDirty.current = false;
          }
        };
      }
    ),
    []
  );
  useEffect(() => {
    if (previewActive) {
      debounceExitPreview();
      return () => {
        debounceExitPreview.cancel();
      };
    }
  }, [debounceExitPreview, previewActive]);
  return {
    previewActive,
    exitPreview
  };
}

// src/components/TextEditor/TextEditorInner.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
function getRangeAtPoint(point, editor) {
  const elementAtPoint = document.elementFromPoint(...point);
  if (!elementAtPoint)
    return null;
  try {
    const range = ReactEditor.findEventRange(editor, {
      clientX: point[0],
      clientY: point[1],
      target: elementAtPoint
    });
    return range;
  } catch (e) {
  }
  return null;
}
var TextEditorInner = observer(function TextEditorInner2({ text, onTextChange, onEscPress }) {
  const { getFocusedPoint: getFocusedPoint2 } = useText();
  const view = useWeakView();
  const { getBounds } = useDesign2();
  const fonts = useWeakFonts();
  const { italic, fontWeight, fontFamily } = text.style;
  const fontStyle = italic ? FontStyle.Italic : FontStyle.Normal;
  const fontLoaded = useFontLoaded({ fontStyle, fontWeight, fontFamily });
  const [editor] = useState2(() => withHistory(withReact(createEditor())));
  const value = useMemo(
    () => textBlocksToSlateElements(text.blocks),
    [text.blocks]
  );
  const { previewActive, exitPreview } = useStylePreview(text);
  useLayoutEffect(() => {
    var _a;
    if (!fontLoaded)
      return;
    const focusedPoint = getFocusedPoint2();
    let range = [];
    if (focusedPoint !== null) {
      range = (_a = getRangeAtPoint(focusedPoint, editor)) != null ? _a : [];
    }
    Transforms.deselect(editor);
    Transforms.select(editor, range);
    ReactEditor.focus(editor);
    requestAnimationFrame(() => {
      Transforms.deselect(editor);
      Transforms.select(editor, range);
      ReactEditor.focus(editor);
    });
  }, [fontLoaded, editor, getFocusedPoint2]);
  const onSlateContentChange = useCallback2(
    (content) => {
      exitPreview();
      if (!ReactEditor.isFocused(editor))
        return;
      const textBlocks = slateElementsToTextBlocks(content);
      onTextChange(text.id, textBlocks);
    },
    [exitPreview, editor, onTextChange, text.id]
  );
  const onKeyDown = useCallback2(
    (keyboardEvent) => {
      const event = keyboardEvent;
      event.stopPropagation();
      const isUndo = isHotkey("mod+z");
      const isRedo = isHotkey("shift+mod+z");
      if (isUndo(event)) {
        handleEditorHistoryEvent(editor, event, "undo");
      }
      if (isRedo(event)) {
        handleEditorHistoryEvent(editor, event, "redo");
      }
      if (event.key === "Escape") {
        onEscPress();
      }
    },
    [editor, onEscPress]
  );
  const handleBlur = useCallback2(() => {
    ReactEditor.deselect(editor);
  }, [editor]);
  if (!fontLoaded)
    return null;
  const bounds = getBounds(text.id, true);
  if (!bounds)
    return null;
  const { fontFaces } = fonts;
  const positionStyle = {
    position: "absolute",
    transform: `matrix(${view.matrix.join(", ")}) translate(${bounds.point[0]}px, ${bounds.point[1]}px) `,
    transformOrigin: "0% 0%",
    outline: previewActive ? "none" : `1px solid ${colors.marvel}`
  };
  const editorStyle = __spreadValues({
    width: "100%",
    height: "100%",
    minWidth: "20px",
    WebkitUserSelect: "text",
    userSelect: "text",
    whiteSpace: "inherit"
  }, TextUtils2.computeTextStylesCss(text, bounds.size, fontFaces));
  return /* @__PURE__ */ jsx2(Slate, { editor, onChange: onSlateContentChange, value, children: /* @__PURE__ */ jsx2("div", { style: positionStyle, children: /* @__PURE__ */ jsx2(
    Editable,
    {
      "data-testid": "text-editor-content",
      onBlur: handleBlur,
      onFocus: exitPreview,
      onKeyDown,
      renderElement: renderBlock,
      style: editorStyle,
      tabIndex: 0
    }
  ) }) });
});
function renderBlock(p) {
  return /* @__PURE__ */ jsx2("p", __spreadProps(__spreadValues({}, p.attributes), { style: { margin: 0 }, children: p.children }));
}
function textBlocksToSlateElements(blocks) {
  return blocks.map((block) => ({
    type: "paragraph" /* Paragraph */,
    children: [{ text: block.text }]
  }));
}
function slateElementsToTextBlocks(nodes) {
  const blocks = nodes.map((node) => ({
    // Currently as we don't have inline styles we can assume
    // children represent a single block of text
    text: Node.string(node)
  }));
  return blocks;
}
function handleEditorHistoryEvent(editor, event, type) {
  event.stopPropagation();
  if (type === "undo") {
    return HistoryEditor.undo(editor);
  }
  if (type === "redo") {
    return HistoryEditor.redo(editor);
  }
}

// src/components/TextEditor/index.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var TextEditor = observer2(function TextEditor2() {
  const editorRef = useRef2(null);
  const { blur: blur2, getFocusedItem: getFocusedItem2 } = useText();
  const { setTextBlocksContent } = useDesign3();
  const textItem = getFocusedItem2();
  const closeTextEditor = useTransactionCallback(() => {
    if (textItem) {
      blur2();
    }
  }, [blur2, textItem]);
  const onTextChange = useTransactionCallback(
    (id, textBlocks) => {
      setTextBlocksContent({
        id,
        textBlocks
      });
    },
    [setTextBlocksContent]
  );
  useOnClickOutside(editorRef, closeTextEditor);
  if (!textItem) {
    return null;
  }
  return textItem ? /* @__PURE__ */ jsx3("div", { "data-testid": "text-editor", ref: editorRef, children: /* @__PURE__ */ jsx3(
    TextEditorInner,
    {
      onEscPress: closeTextEditor,
      onTextChange,
      onUnmount: closeTextEditor,
      text: textItem
    },
    textItem.id
  ) }) : null;
});

// src/hocs/autoSizableText.tsx
import {
  PointUtils,
  TextSizing,
  TextUtils as TextUtils3
} from "@ballpark/design-creator";
import {
  useDesign as useDesign4,
  useTransactionCallback as useTransactionCallback2
} from "@ballpark/design-plugin-react";
import { observer as observer3 } from "mobx-react";
import React4, { useLayoutEffect as useLayoutEffect2, useState as useState3 } from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
function autoSizableText(BaseText, direct = false) {
  const AutoSizedText = observer3(function AutoSizedText2(props) {
    const { layoutHash, setTextSize } = useDesign4();
    const [textEl, setTextEl] = useState3(null);
    const { data: text } = props;
    const autoSizeText = useTransactionCallback2(
      (entries) => {
        if (!entries.length)
          return;
        const currentLayoutHash = TextUtils3.getLayoutHash(text);
        const newLayoutHash = layoutHash(text);
        if (currentLayoutHash === newLayoutHash)
          return;
        const { inlineSize: width, blockSize: height } = entries[0].borderBoxSize[0];
        const size = TextUtils3.getSize(text);
        const [sizingX, sizingY] = TextUtils3.getSizing(text);
        const isAutoX = sizingX === TextSizing.Auto;
        const isAutoY = sizingY === TextSizing.Auto;
        const newSize = [
          isAutoX ? width : size[0],
          isAutoY ? height : size[1]
        ];
        if (direct) {
          if (!PointUtils.isCloseTo(text.size, newSize))
            text.size = newSize;
          TextUtils3.mSetLayoutHash(text, newLayoutHash);
        } else {
          setTextSize({
            id: text.id,
            size: newSize,
            layoutHash: newLayoutHash
          });
        }
      },
      [layoutHash, setTextSize, text],
      "auto"
      // prevents this change from being considered by the UndoManager if one exists
    );
    useLayoutEffect2(() => {
      if (!textEl)
        return;
      const resizeObserver = new ResizeObserver(autoSizeText);
      resizeObserver.observe(textEl);
      return () => {
        resizeObserver.disconnect();
      };
    }, [autoSizeText, textEl]);
    return /* @__PURE__ */ jsx4(BaseText, __spreadProps(__spreadValues({}, props), { ref: setTextEl }));
  });
  const MaybeAutoSizedText = observer3(
    React4.forwardRef(function MaybeAutoSizedText2(props, ref) {
      const [sizingX, sizingY] = TextUtils3.getSizing(props.data);
      const isAutoX = sizingX === TextSizing.Auto;
      const isAutoY = sizingY === TextSizing.Auto;
      const hasAuto = isAutoX || isAutoY;
      if (hasAuto) {
        return /* @__PURE__ */ jsx4(AutoSizedText, __spreadProps(__spreadValues({}, props), { ref }));
      }
      return /* @__PURE__ */ jsx4(BaseText, __spreadProps(__spreadValues({}, props), { ref }));
    })
  );
  return MaybeAutoSizedText;
}

// src/hocs/editableText.tsx
import { useTransactionCallback as useTransactionCallback3 } from "@ballpark/design-plugin-react";
import { useComponents } from "@ballpark/design-renderer-base";
import { observer as observer4 } from "mobx-react";
import React5 from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
function editableText(BaseText) {
  const EditableText = observer4(
    React5.forwardRef(function EditableText2(props, ref) {
      const { Interaction } = useComponents();
      const { focus: focus2, isFocused: isFocused2 } = useText();
      const { data } = props;
      const onPointerDown = useTransactionCallback3(
        (event) => {
          if (isFocused2()) {
            event.stopPropagation();
            focus2(data.id, [event.clientX, event.clientY]);
          }
        },
        [data, focus2, isFocused2]
      );
      const onDoubleClick = useTransactionCallback3(() => {
        const { id } = data;
        focus2(id);
      }, [data, focus2]);
      if (isFocused2(data.id))
        return null;
      return /* @__PURE__ */ jsx5(
        Interaction,
        {
          onDoubleClick,
          onPointerDown,
          children: /* @__PURE__ */ jsx5(BaseText, __spreadProps(__spreadValues({}, props), { ref }))
        }
      );
    })
  );
  return autoSizableText(EditableText);
}
export {
  TextEditor,
  TextProvider,
  WeakTextProvider,
  autoSizableText,
  blur,
  createTextState,
  editableText,
  focus,
  getFocusedId,
  getFocusedItem,
  getFocusedPoint,
  isFocused,
  useText,
  useWeakText
};
